import React, { useEffect, useState } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Lenis from 'lenis';
import { LanguageProvider } from '@/context/LanguageContext';
import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import TrustZone from '@/components/TrustZone';
import Marquee from '@/components/Marquee';
import PlotLayout from '@/components/PlotLayout';
import PlotConfigurations from '@/components/PlotConfigurations';
import InfrastructureGrid from '@/components/InfrastructureGrid';
import LocationMatrix from '@/components/LocationMatrix';
import SiteVisitForm from '@/components/SiteVisitForm';
import EMICalculator from '@/components/EMICalculator';
import MapSection from '@/components/MapSection';
import BrochureModal from '@/components/BrochureModal';
import StickyFooter from '@/components/StickyFooter';
import PlotVisualizerModal from '@/components/PlotVisualizerModal';
import VaastuClaimModal from '@/components/VaastuClaimModal';
import AdminPage from '@/components/AdminPage';
import plotData from '@/data/plotData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LandingPage() {
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [visualizerOpen, setVisualizerOpen] = useState(false);
  const [vaastuOpen, setVaastuOpen] = useState(false);
  const [vaastuPlot, setVaastuPlot] = useState(null);
  const [plots, setPlots] = useState(plotData);
  const [visualizerDefaultTab, setVisualizerDefaultTab] = useState('standard');

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  // Fetch plot status overrides from admin
  useEffect(() => {
    fetch(`${API}/plots`).then(r => r.json()).then(data => {
      if (data.overrides?.length) {
        const overrideMap = {};
        data.overrides.forEach(o => { overrideMap[o.plot_id] = o.status; });
        setPlots(plotData.map(p => overrideMap[p.id] ? { ...p, status: overrideMap[p.id] } : p));
      }
    }).catch(() => {});
  }, []);

  // Handle shared plot link (?share=TOKEN)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareToken = params.get('share');
    if (!shareToken) return;

    fetch(`${API}/s/${shareToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.plot_id) {
          const plot = plotData.find(p => p.id === data.plot_id);
          if (plot) {
            setSelectedPlot(plot);
            setVisualizerDefaultTab('imagine');
            setVisualizerOpen(true);
            // Scroll to plots section after a short delay
            setTimeout(() => {
              document.getElementById('plots')?.scrollIntoView({ behavior: 'smooth' });
            }, 500);
          }
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlotClick = (plot) => {
    if (plot.status === 'sold') return;
    // Vaastu plots bypass visualizer — go straight to priority claim
    if (plot.vaastu) {
      setVaastuPlot(plot);
      setVaastuOpen(true);
      return;
    }
    setSelectedPlot(plot);
    setVisualizerDefaultTab('standard');
    setVisualizerOpen(true);
  };

  const handleVaastuClick = (plot) => {
    setVaastuPlot(plot);
    setVaastuOpen(true);
  };

  const handleBookVisitFromVisualizer = (plotId) => {
    setVisualizerOpen(false);
    setTimeout(() => {
      document.getElementById('site-visit')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <LanguageProvider>
      <div className="App bg-[#FDFBF7] min-h-screen">
        <Header />
        <HeroSection onOpenBrochure={() => setBrochureOpen(true)} />
        <TrustZone />
        <Marquee />
        <PlotConfigurations />
        <PlotLayout
          plots={plots}
          onPlotClick={handlePlotClick}
          onVaastuClick={handleVaastuClick}
        />
        <InfrastructureGrid />
        <LocationMatrix />
        <EMICalculator />
        <SiteVisitForm />
        <MapSection />
        <StickyFooter onOpenBrochure={() => setBrochureOpen(true)} />
        <BrochureModal open={brochureOpen} onOpenChange={setBrochureOpen} />
        <PlotVisualizerModal
          plot={selectedPlot}
          open={visualizerOpen}
          onOpenChange={setVisualizerOpen}
          onBookVisit={handleBookVisitFromVisualizer}
          defaultTab={visualizerDefaultTab}
        />
        <VaastuClaimModal
          plot={vaastuPlot}
          open={vaastuOpen}
          onOpenChange={setVaastuOpen}
        />
      </div>
    </LanguageProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
